import { DataTable } from "primereact/datatable";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { InspectionsTableFields } from "../inspections-table/Fields";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
    getInspectionsThunk,
    inspectionsSelector
} from "../../features/inspectionsSlice";
import {
    MultiSelectTemplate,
    RowWithSubtext,
    successToast,
    vesselBodyTemplate,
    warningToast
} from "@idwal/idwal-react-components";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useRef, useState } from "react";
import { getUserInfoThunk, userSliceSelector } from "../../features/userSlice";
import { getInspectionDate } from "../../utils/dates";
import { FilterMatchMode } from "primereact/api";
import { Inspection } from "../../types/inspections/Inspection";
import { InspectionDateFilterValue } from "../../types/inspections/InspectionDateFilterValue";
import {
    CANCELLED_STATUS,
    FULL_REPORT_STATUS,
    PURCHASED_REPORT_SHORT_NAME
} from "../inspections-table/InspectionsTable";
import {
    useInspectionDateColumn,
    useInspectionTypeColumn,
    useVesselColumn,
    useVesselTypeColumn
} from "../shared-columns/shared-columns";
import {
    inProgressTableGlobalFilterFields,
    inspectionDateFilterOptions
} from "../table-header/FilterSetup";
import { useDebounceCallback } from "usehooks-ts";
import TableHeader from "../table-header/TableHeader";
import { INSPECTION_DATE_FILTER_FIELD } from "../inspections-table/FilterFields";
import {
    DEFAULT_ROWS_PER_PAGE,
    DEFAULT_ROWS_PER_PAGE_OPTIONS
} from "../../constants/TableConstants";
import { OrdersBodyTemplate } from "../inspections-table/OrdersBodyTemplate";
import { downloadFile } from "../../utils/downloadFile";

const formatter = new Intl.NumberFormat("en-US");
const IN_PROGRESS_TABLE_KEY = "inprogressTable";

const defaultFilters = () => ({
    global: { value: "", matchMode: FilterMatchMode.CONTAINS },
    vesselNameFilterField: { value: null, matchMode: FilterMatchMode.CONTAINS },
    inspectionStatusFriendlyName: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS
    },
    inspectionType: { value: null, matchMode: FilterMatchMode.IN },
    inspectionDateFilterField: {
        value: null,
        matchMode: FilterMatchMode.CUSTOM
    },
    vesselType: { value: null, matchMode: FilterMatchMode.IN }
});

export interface InProgressTableRow {
    vesselName: string;
    imo: string;
    vesselNameFilterField: string;
    inspectionDateFilterField: InspectionDateFilterValue;
    inspectionType: string;
    inspectionNumber: string;
    port: string;
    inspectionDate: string;
    inspectionStatusFriendlyName: string;
    inspectionStatus: string;
    vesselType: string;
    inspectionDateSortField: string;
    totalPrice: string;
    totalPriceSortField: number | undefined;
    earlyRelease: boolean;
    guid: string;
    location: string;
    quotationId: string;
}

function InProgressTable() {
    const { t } = useTranslation("locale");
    const { Inspections, InspectionsStatus } =
        useAppSelector(inspectionsSelector);
    const {
        UserInfoStatus,
        SelectedGroupName,
        OrganisationsInfo,
        OrganisationsInfoStatus
    } = useAppSelector(userSliceSelector);
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);
    const dispatch = useAppDispatch();
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(DEFAULT_ROWS_PER_PAGE);
    const ref = useRef(null);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
    const [_globalFilterValue, setGlobalFilterValue] = useState("");
    const debounced = useDebounceCallback((value: string) => {
        const _filters = { ...filters };
        _filters["global"].value = value;
        setFilters(_filters);
    }, 300);

    const onPage = (event: any) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const [filters, setFilters] = useState(defaultFilters());

    useEffect(() => {
        const requestsSuccessful =
            UserInfoStatus === "succeeded" &&
            OrganisationsInfoStatus === "succeeded";
        if (!requestsSuccessful) {
            return;
        }

        const inspectionsForSelectedOrg = Inspections?.OrgIds.includes(
            SelectedGroupName !== ""
                ? SelectedGroupName
                : OrganisationsInfo[0].id.toString()
        );
        if (
            requestsSuccessful &&
            (!inspectionsForSelectedOrg || Inspections === null)
        ) {
            dispatch(
                getInspectionsThunk({
                    orgIds: [
                        SelectedGroupName !== ""
                            ? SelectedGroupName
                            : OrganisationsInfo[0].id.toString()
                    ],
                    user
                })
            );
        }
    }, [dispatch, UserInfoStatus, SelectedGroupName, OrganisationsInfo]);

    useEffect(() => {
        if (user && UserInfoStatus === "idle") {
            dispatch(getUserInfoThunk({ userId: user.username! }));
        }
    }, [user]);

    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;

        setDebouncedSearchValue(value);
        debounced.cancel();
        debounced(value);
    };

    const inspectionFilter = (inspection: Inspection) =>
        inspection.status !== FULL_REPORT_STATUS &&
        inspection.status !== CANCELLED_STATUS &&
        inspection.inspectionTypeShortName !== PURCHASED_REPORT_SHORT_NAME;

    const values: InProgressTableRow[] =
        Inspections !== null
            ? Inspections.Inspections.filter(inspectionFilter).map(
                  (inspection) => ({
                      vesselName: inspection.vesselName,
                      vesselNameFilterField: `${inspection.vesselName} ${inspection.vesselImo}`,
                      inspectionDateFilterField: {
                          dateFrom: inspection.inspectionDateFrom,
                          dateTo: inspection.inspectionDateTo
                      },
                      vesselType: inspection.vesselType,
                      imo: inspection.vesselImo,
                      inspectionType: inspection.inspectionType,
                      inspectionNumber: inspection.inspectionNumber,
                      port: inspection.port,
                      inspectionDate: getInspectionDate(
                          inspection.inspectionDateFrom,
                          inspection.inspectionDateTo,
                          inspection.status
                      ),
                      inspectionStatus: inspection.status,
                      inspectionStatusFriendlyName: t(
                          `quotations.${inspection.status}`
                      ),
                      inspectionDateSortField: inspection.inspectionDateFrom,
                      totalPrice: inspection.totalPrice
                          ? `$${formatter.format(
                                parseFloat(inspection.totalPrice)
                            )}`
                          : "",
                      totalPriceSortField: inspection.totalPrice
                          ? parseFloat(inspection.totalPrice)
                          : undefined,
                      earlyRelease: inspection.earlyRelease,
                      guid: inspection.id,
                      location:
                          inspection.status == "UPCOMING"
                              ? ""
                              : inspection.port + ", " + inspection.country,
                      quotationId: inspection.quotationId
                  })
              )
            : [];

    const getValues = (): InProgressTableRow[] => values;

    const distinctTypes = Array.from(
        new Set([
            ...values.flatMap((e: InProgressTableRow) => e.inspectionType)
        ])
    );
    const distinctInspectionStatuses = Array.from(
        new Set([
            ...values.flatMap((e: InProgressTableRow) =>
                t(`quotations.${e.inspectionStatus}`)
            )
        ])
    );
    const distinctVesselTypes = Array.from(
        new Set([...values.flatMap((e: InProgressTableRow) => e.vesselType)])
    );

    const resetTable = () => {
        sessionStorage.removeItem(IN_PROGRESS_TABLE_KEY);
        // @ts-ignore due to type difficulties with ref.current.reset - I don't think it's an important enough type to spend too long researching
        ref.current?.reset();
        onFilterFunction({ filters: defaultFilters() });
    };

    const onFilterFunction = (e: any) => {
        setFilters(e.filters);
        setDebouncedSearchValue(e.filters.global.value);
    };

    const downloadConfirmationInspection = async (
        rowData: InProgressTableRow
    ) => {
        successToast(t("report.downloadStarted"));
        const baseUrl = "https://" + process.env.REACT_APP_ASIMS_ENDPOINT;
        const pdfUrl =
            baseUrl +
            "/public/Quotation/Confirmation/" +
            rowData.quotationId.toLowerCase();
        const downloadName = rowData.vesselName + "_CONFIRMATION.pdf"; // Set the name for the downloaded file;

        downloadFile(pdfUrl, downloadName, t);
    };

    return (
        <div
            className={"table-container home-table"}
            data-cy="inspections-table-container"
        >
            <DataTable
                globalFilterFields={inProgressTableGlobalFilterFields}
                // @ts-ignore due to type difficulties with ref.current.reset - I don't think it's an important enough type to spend too long researching
                header={TableHeader({
                    setGlobalFilterValue,
                    onGlobalFilterChange,
                    globalFilterValue: debouncedSearchValue,
                    resetTable
                })}
                emptyMessage={t("inspections.table.emptyTable")}
                removableSort
                stripedRows
                paginator
                first={first}
                onPage={onPage}
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                scrollHeight="55vh"
                scrollable
                rows={rows}
                filters={filters}
                filterDisplay="row"
                stateStorage="session"
                stateKey={IN_PROGRESS_TABLE_KEY}
                onFilter={onFilterFunction}
                value={getValues()}
                sortField={InspectionsTableFields.INSPECTION_DATE_SORT_FIELD}
                sortOrder={-1}
                loading={InspectionsStatus === "pending"}
                ref={ref}
            >
                {useVesselColumn(
                    InspectionsTableFields.VESSEL,
                    (data: InProgressTableRow) =>
                        vesselBodyTemplate(data, () => {}, false)
                )}
                <Column
                    field={InspectionsTableFields.ACTIONS}
                    header={t("inspections.table.actions")}
                    body={(data: InProgressTableRow) =>
                        OrdersBodyTemplate(
                            data,
                            data.earlyRelease,
                            (data: InProgressTableRow) =>
                                window.open(
                                    `/inspection/${data.guid}`,
                                    "_blank"
                                ),
                            (data: InProgressTableRow) => {
                                downloadConfirmationInspection(data);
                            },
                            t
                        )
                    }
                />
                <Column
                    field={InspectionsTableFields.TOTAL_PRICE_SORT_FIELD}
                    header={t("inspections.table.totalPriceHeader")}
                    body={(data: InProgressTableRow) => data.totalPrice}
                    sortable
                />
                {useInspectionTypeColumn(distinctTypes)}
                <Column
                    field={InspectionsTableFields.STATUS_FRIENDLY_NAME}
                    header={t("inspections.table.statusHeader")}
                    filter
                    showFilterMenu={false}
                    filterElement={(options) =>
                        MultiSelectTemplate(
                            options,
                            distinctInspectionStatuses,
                            t("inspections.table.showAll"),
                            "vessel-type-filter"
                        )
                    }
                    sortable
                />
                {useVesselTypeColumn(
                    InspectionsTableFields.VESSEL_TYPE,
                    distinctVesselTypes
                )}
                {useInspectionDateColumn(
                    InspectionsTableFields.INSPECTION_DATE_SORT_FIELD,
                    INSPECTION_DATE_FILTER_FIELD,
                    (data: InProgressTableRow) =>
                        RowWithSubtext(
                            data,
                            InspectionsTableFields.INSPECTION_DATE,
                            InspectionsTableFields.LOCATION
                        ),
                    inspectionDateFilterOptions
                )}
            </DataTable>
        </div>
    );
}

export default InProgressTable;
